import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "../button/wow-button";
import { getAssetUrl } from "helpers/assetHelpers";
import { NotificationService } from "../notification/notification-service";
import { SignalElement, inject } from "ui-common/di";
import { resolve } from "ui-common/di/decorators";
import { DiContainer } from "ui-common/di/di-container";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { INotificationTranslationService } from "ui-common/services/notification-translation/types";
import { NOTIFICATION_KEYS } from "ui-common/services/notification-translation/notification-keys";

import { notificationCardStyles } from "./notification-card.css";

export type NotificationCardData = {
  id: string;
  type: string;
  text: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  isRead: boolean;
  readAt?: string;
  creatorHandle?: string;
  creatorAvatarUrl?: string | null;
  creatorFirstName?: string;
  creatorLastName?: string;
};

interface RpcActionButton {
  text: string;
  type: "rpc" | "url";
  action: string;
  verified?: boolean;
}

@customElement("notification-card")
export class NotificationCard extends SignalElement {
  static override styles = notificationCardStyles;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @property({ type: Object }) notification!: NotificationCardData;

  @state() private retrySeconds = 0;
  private retryInterval?: number;

  @state() private isCtaLoading = false;

  private get translationService(): INotificationTranslationService | null {
    if (DiContainer.isInitialized() && DiContainer.has("notificationTranslationService")) {
      return resolve<INotificationTranslationService>("notificationTranslationService");
    }
    return null;
  }

  private translateText(text: string, metadata?: Record<string, unknown>): string {
    return this.translationService?.translateText(text, metadata) ?? text;
  }

  private translateCta(ctaText: string): string {
    return this.translationService?.translateCta(ctaText) ?? ctaText;
  }

  private get isCreatorNotification(): boolean {
    return (
      this.notification.type === "CREATOR-TO-FAN" &&
      !!(this.notification.creatorFirstName && this.notification.creatorLastName)
    );
  }

  private get creatorFullName(): string {
    if (!this.notification.creatorFirstName || !this.notification.creatorLastName) {
      return "";
    }
    return `${this.notification.creatorFirstName} ${this.notification.creatorLastName}`;
  }

  private startRetryCountdown(seconds: number) {
    this.retrySeconds = seconds;

    this.retryInterval = window.setInterval(() => {
      this.retrySeconds -= 1;

      if (this.retrySeconds <= 0) {
        clearInterval(this.retryInterval);
        this.retryInterval = undefined;
      }

      this.requestUpdate();
    }, 1000);
  }

  private async _handleActionClick(event: Event) {
    event.stopPropagation();

    const actionButton = this._getActionButton();
    if (!actionButton || actionButton.verified) return;

    // If still in cooldown, ignore click
    if (this.retrySeconds > 0) return;

    this.isCtaLoading = true;

    try {
      if (actionButton.type === "rpc") {
        const result = await this.rpcService.call<Record<string, unknown>>(actionButton.action, {});

        if (result && typeof result === "object" && "email" in result) {
          const email = result.email as string;
          const message =
            this.translationService?.translateToast(NOTIFICATION_KEYS.TOAST_VERIFICATION_EMAIL_SENT, { email }) ??
            `Verification email sent to ${email}`;
          NotificationService.success(message);
        }

        this.dispatchEvent(
          new CustomEvent("notification-action-success", {
            bubbles: true,
            composed: true,
            detail: { notification: this.notification, actionType: "rpc", actionValue: actionButton.action },
          }),
        );

        // 🔥 Start 30-sec retry countdown
        this.startRetryCountdown(30);
      } else if (actionButton.type === "url") {
        window.location.href = actionButton.action;
      }
    } catch (error) {
      console.error("Error executing notification action:", error);

      this.dispatchEvent(
        new CustomEvent("notification-action-error", {
          bubbles: true,
          composed: true,
          detail: {
            notification: this.notification,
            error: error instanceof Error ? error.message : "Unknown error",
            actionType: actionButton.type,
            actionValue: actionButton.action,
          },
        }),
      );
    } finally {
      this.isCtaLoading = false;
    }
  }

  private _getActionButton(): RpcActionButton | null {
    const metadata = this.notification.metadata;
    if (!metadata || metadata.verified) return null;

    if (metadata.ctaText && metadata.ctaType) {
      const ctaType = metadata.ctaType as string;

      if (ctaType === "rpc" && metadata.rpcName) {
        return {
          text: metadata.ctaText as string,
          type: "rpc",
          action: metadata.rpcName as string,
          verified: metadata.verified as boolean,
        };
      } else if (ctaType === "url" && metadata.url) {
        return {
          text: metadata.ctaText as string,
          type: "url",
          action: metadata.url as string,
        };
      }
    }

    return null;
  }

  private renderCreatorAvatar() {
    const avatarUrl = this.notification.creatorAvatarUrl;
    const creatorName = this.creatorFullName || "Creator";

    if (!avatarUrl) {
      return html`<div class="creator-avatar-fallback">${creatorName.charAt(0).toUpperCase()}</div>`;
    }

    return html`<img src=${getAssetUrl(avatarUrl)} alt=${creatorName} class="creator-avatar-img" />`;
  }

  private getButtonText(actionButton: RpcActionButton): string {
    if (this.isCtaLoading) {
      return this.translateCta("Loading...");
    }
    if (this.retrySeconds > 0) {
      return this.translateCta(`Try again in ${this.retrySeconds}s`);
    }
    return this.translateCta(actionButton.text);
  }

  protected override render() {
    if (!this.notification) {
      return nothing;
    }

    const cardClasses = {
      "notification-card": true,
      unread: !this.notification.isRead,
    };

    const actionButton = this._getActionButton();
    const translatedText = this.translateText(this.notification.text, this.notification.metadata);

    return html`
      <div class="${classMap(cardClasses)}">
        ${this.isCreatorNotification
          ? html`<div class="creator-avatar">${this.renderCreatorAvatar()}</div>`
          : html`<app-logo class="notification-logo"></app-logo>`}

        <div class="notification-content">
          ${this.isCreatorNotification
            ? html`
                <div class="creator-name">${this.creatorFullName}</div>
                <div class="notification-text">${translatedText}</div>
              `
            : html`<div class="notification-text">${translatedText}</div>`}
        </div>

        ${actionButton
          ? this.notification.isRead
            ? html`
                <wow-button
                  backgroundColor="var(--sds-color-background-default-tertiary)"
                  color="var(--sds-color-text-default-default)"
                  class="notification-action-button"
                  size="small"
                  ?disabled=${this.isCtaLoading || this.retrySeconds > 0}
                  @click=${this._handleActionClick}
                >
                  ${this.getButtonText(actionButton)}
                </wow-button>
              `
            : html`
                <wow-button
                  variant="primary"
                  class="notification-action-button"
                  size="small"
                  ?disabled=${this.isCtaLoading || this.retrySeconds > 0}
                  @click=${this._handleActionClick}
                >
                  ${this.getButtonText(actionButton)}
                </wow-button>
              `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "notification-card": NotificationCard;
  }
}
